import { WSClient, ApiMessage } from "@/ems/WSClient";
import { getFirstParam, parseIntId } from "@/ems/utils";
import router from "@/router/index";

/**
 * Checks if the user has any of the specified permissions for the current series.
 * @param permissions - An array of permission names to check.
 * @returns A promise that resolves to true if the user has any of the permissions, otherwise redirects to "NotAllowedInSerie".
 */
export async function checkUserSerieOrPermissions(permissions: string[]): Promise<boolean> {
    const local_client = new WSClient();

    const route = router.currentRoute.value,
        serie_id = parseIntId(getFirstParam(route.params.series_id) ?? "", 0);

    const resp = await local_client.queryWs<Record<string, boolean>>("GET", "user_permission", {
        sid: serie_id,
        permission_name: permissions.join("|"),
    });
    for (let index = 0; index < permissions.length; index++) {
        const permission = permissions[index];
        if (resp[permission] ?? false) return true;
    }
    return router.push({ name: "NotAllowedInSerie" });
}

/**
 * Checks if the user has a specific permission for the current series.
 * @param permission - The permission name to check.
 * @returns A promise that resolves to true if the user has the permission, otherwise redirects to "NotAllowedInSerie".
 */
export async function checkUserSeriePermission(permission: string): Promise<boolean> {
    const local_client = new WSClient();

    const route = router.currentRoute.value,
        serie_id = parseIntId(getFirstParam(route.params.series_id) ?? "", 0);

    const resp = await local_client.queryWs<Record<string, boolean>>("GET", "user_permission", {
        sid: serie_id,
        permission_name: permission,
    });
    const allowed = resp[permission] ?? false;
    if (allowed) return true;
    return router.push({ name: "NotAllowedInSerie" });
}

/**
 * Checks if the user has a specific permission regardless of the series.
 * @param permission - The permission name to check.
 * @returns A promise that resolves to true if the user has the permission, otherwise false.
 */
export async function checkUserHasPermission(permission: string, hs_event: boolean = false): Promise<boolean> {
    const local_client = new WSClient();

    const route = router.currentRoute.value,
        serie_id = parseIntId(getFirstParam(route.params.series_id) ?? "", 0);

    const resp = await local_client.queryWs<Record<string, boolean>>("GET", "user_permission", {
        sid: serie_id,
        permission_name: permission,
        hs_event: hs_event,
    });
    return resp[permission] ?? false;
}

/**
 * Retrieves user permissions from the server.
 * @param client - The WSClient instance to use for the request.
 * @param permissions - A single permission name or an array of permission names.
 * @param serie_id - An optional series ID to filter permissions.
 * @returns A promise that resolves to a record of permission names and their boolean values.
 */
export function getUserPermission(
    client: WSClient,
    permissions: string | string[],
    serie_id: string | number | undefined = undefined
): Promise<Record<string, boolean>> {
    if (Array.isArray(permissions)) permissions = permissions.join("|");

    const params = {
        permission_name: permissions,
    } as Record<string, string | number>;

    if (serie_id) params.model_id = serie_id;

    return client.queryWs<Record<string, boolean>>("GET", "user_permission", params);
}

/**
 * Represents a user role with various attributes.
 */
export interface Role {
    id: number;                // Unique identifier for the role
    name: string;              // Name of the role
    display_name: string;      // Display name of the role
    description: string;       // Description of the role
    author: number;            // ID of the author who created the role
    created_at: string;        // Timestamp of when the role was created
    updated_at: string;        // Timestamp of when the role was last updated
    isPlan: number;            // Indicates if the role is a plan
    type: any;                 // Type of the role
    model_type: string;        // Type of the model associated with the role
    model_id: number;          // ID of the model associated with the role
    organization_id: any;      // ID of the organization associated with the role
}

/**
 * Retrieves the roles assigned to the user.
 * @param client - The WSClient instance to use for the request.
 * @returns A promise that resolves to an array of Role objects.
 */
export function getUserRoles(client: WSClient): Promise<Role[]> {
    return client.queryWs<{ roles: Role[]; messages: ApiMessage[] }>("GET", "user_UserRole").then((res) => {
        return res.roles;
    });
}
